import { useEffect, useMemo, useState } from "react";
import { useStore, formatCOP } from "../lib/store";
import { SERVICE_CATEGORIES, BUSINESS, SERVICES, PROMOTIONS } from "../data/siteData";
import { Lock, LogOut, Save, Plus, Trash2, RotateCcw, Eye, EyeOff, Settings, Tag, Sparkles, ListChecks, ExternalLink, Smartphone } from "lucide-react";
import { WhatsappFloat } from "../components/WhatsappFloat";

const ADMIN_PASSWORD = "Zaida9925"; // sencillo (estático)

const TABS = [
  { id: "services", label: "Servicios", icon: <Sparkles size={16} /> },
  { id: "promos", label: "Promociones", icon: <Tag size={16} /> },
  { id: "business", label: "Información", icon: <Settings size={16} /> },
  { id: "reservations", label: "Reservas", icon: <ListChecks size={16} /> },
];

export default function AdminPage() {
  const [logged, setLogged] = useState(() => sessionStorage.getItem("aura_admin") === "1");
  return (
    <div className="min-h-screen bg-beige-50">
      {!logged ? <Login onLogin={() => setLogged(true)} /> : <Dashboard onLogout={() => { sessionStorage.removeItem("aura_admin"); setLogged(false); }} />}
      <WhatsappFloat />
    </div>
  );
}

function Login({ onLogin }) {
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (pwd === ADMIN_PASSWORD) {
      sessionStorage.setItem("aura_admin", "1");
      onLogin();
    } else {
      setErr("Contraseña incorrecta");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-3xl border border-beige-200 p-8 md:p-10 shadow-xl" data-testid="admin-login-form">
        <div className="w-12 h-12 rounded-full bg-gold-50 flex items-center justify-center mx-auto mb-5">
          <Lock size={20} className="text-gold" />
        </div>
        <h1 className="font-serif text-3xl text-ink text-center mb-2">Panel Admin</h1>
        <p className="text-sm text-ink-soft text-center mb-7 font-light">Ingresa para administrar el sitio.</p>

        <label className="block mb-5">
          <div className="text-xs tracking-luxe uppercase text-ink-soft mb-2">Contraseña</div>
          <div className="relative">
            <input
              data-testid="admin-password-input"
              type={show ? "text" : "password"}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="w-full px-4 py-3 pr-12 rounded-xl border border-beige-200 bg-beige-50 focus:bg-white focus:border-gold focus:outline-none focus:ring-4 focus:ring-gold/10 transition-all"
              placeholder="••••••••"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-soft hover:text-gold"
              aria-label={show ? "Ocultar" : "Mostrar"}
            >
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        {err && <div className="text-sm text-red-500 mb-4" data-testid="admin-login-error">{err}</div>}

        <button type="submit" data-testid="admin-login-submit" className="w-full btn-gold py-3.5">Ingresar</button>
        <p className="text-xs text-center text-ink-soft mt-4 font-light">
          Acceso restringido para administración del sitio.
        </p>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("services");
  const { resetAll } = useStore();

  return (
    <div>
      {/* Topbar */}
      <header className="bg-white border-b border-beige-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-ink flex items-center justify-center">
              <span className="font-serif text-gold">A</span>
            </div>
            <div>
              <div className="font-serif text-lg text-ink">AURA Admin</div>
              <div className="text-[10px] tracking-luxe uppercase text-ink-soft">Panel de control</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-beige-300 text-sm text-ink-soft hover:text-gold hover:border-gold transition-colors" data-testid="admin-view-site">
              <ExternalLink size={14} /> Ver sitio
            </a>
            <button onClick={() => { if (window.confirm("Esto restablecerá toda la información a los valores por defecto. ¿Continuar?")) resetAll(); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-beige-300 text-sm text-ink-soft hover:text-red-500 hover:border-red-300 transition-colors" data-testid="admin-reset">
              <RotateCcw size={14} /> Reset
            </button>
            <button onClick={onLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-white text-sm hover:bg-gold transition-colors" data-testid="admin-logout">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? "border-gold text-ink" : "border-transparent text-ink-soft hover:text-ink"}`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "services" && <ServicesPanel />}
        {tab === "promos" && <PromosPanel />}
        {tab === "business" && <BusinessPanel />}
        {tab === "reservations" && <ReservationsPanel />}
      </main>
    </div>
  );
}

/* ----- Panels ----- */

function ServicesPanel() {
  const { services, updateServices } = useStore();
  const [list, setList] = useState(services);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setList(services); }, [services]);

  const update = (id, key, value) => {
    setList(list.map((s) => (s.id === id ? { ...s, [key]: value } : s)));
    setDirty(true);
  };
  const remove = (id) => {
    if (!window.confirm("¿Eliminar este servicio?")) return;
    setList(list.filter((s) => s.id !== id));
    setDirty(true);
  };
  const add = () => {
    const id = "s-" + Math.random().toString(36).slice(2, 8);
    setList([{ id, category: "corporal", name: "Nuevo servicio", description: "Descripción del servicio", price: 100000, image: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?crop=entropy&cs=srgb&fm=jpg&q=85" }, ...list]);
    setDirty(true);
  };
  const saveAll = () => { updateServices(list); setDirty(false); };

  return (
    <Panel title="Servicios" actions={
      <>
        <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-beige-50 border border-beige-200 text-sm text-ink hover:border-gold transition-colors" data-testid="admin-add-service">
          <Plus size={14} /> Nuevo
        </button>
        <button onClick={saveAll} disabled={!dirty} className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm transition-colors ${dirty ? "bg-gold text-white hover:bg-gold-600" : "bg-beige-200 text-ink-soft cursor-not-allowed"}`} data-testid="admin-save-services">
          <Save size={14} /> Guardar
        </button>
      </>
    }>
      <div className="overflow-x-auto rounded-2xl border border-beige-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-beige-50 text-ink-soft text-xs uppercase tracking-luxe">
            <tr>
              <th className="text-left px-4 py-3">Servicio</th>
              <th className="text-left px-4 py-3">Categoría</th>
              <th className="text-left px-4 py-3">Descripción</th>
              <th className="text-left px-4 py-3">Precio</th>
              <th className="text-left px-4 py-3">Imagen URL</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-t border-beige-100" data-testid={`admin-service-row-${s.id}`}>
                <td className="px-4 py-3"><input className="admin-input" value={s.name} onChange={(e) => update(s.id, "name", e.target.value)} /></td>
                <td className="px-4 py-3">
                  <select className="admin-input" value={s.category} onChange={(e) => update(s.id, "category", e.target.value)}>
                    {SERVICE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3"><textarea rows={2} className="admin-input" value={s.description} onChange={(e) => update(s.id, "description", e.target.value)} /></td>
                <td className="px-4 py-3 w-32"><input type="number" className="admin-input" value={s.price} onChange={(e) => update(s.id, "price", Number(e.target.value))} /><div className="text-[10px] text-ink-soft mt-1">{formatCOP(s.price)}</div></td>
                <td className="px-4 py-3"><input className="admin-input" value={s.image} onChange={(e) => update(s.id, "image", e.target.value)} /></td>
                <td className="px-4 py-3"><button onClick={() => remove(s.id)} className="text-ink-soft hover:text-red-500" data-testid={`admin-remove-service-${s.id}`}><Trash2 size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AdminInputStyles />
    </Panel>
  );
}

function PromosPanel() {
  const { promotions, updatePromotions } = useStore();
  const [list, setList] = useState(promotions);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setList(promotions); }, [promotions]);

  const update = (id, key, value) => { setList(list.map((p) => (p.id === id ? { ...p, [key]: value } : p))); setDirty(true); };
  const remove = (id) => { if (!window.confirm("¿Eliminar promoción?")) return; setList(list.filter((p) => p.id !== id)); setDirty(true); };
  const add = () => {
    const id = "p-" + Math.random().toString(36).slice(2, 8);
    setList([{ id, title: "Nueva promoción", description: "Descripción", discount: "20%", badge: "Oferta", image: "https://images.unsplash.com/photo-1754961968085-1f5799fd7a7c?crop=entropy&cs=srgb&fm=jpg&q=85" }, ...list]);
    setDirty(true);
  };
  const saveAll = () => { updatePromotions(list); setDirty(false); };

  return (
    <Panel title="Promociones" actions={
      <>
        <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-beige-50 border border-beige-200 text-sm hover:border-gold" data-testid="admin-add-promo"><Plus size={14}/> Nueva</button>
        <button onClick={saveAll} disabled={!dirty} className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm ${dirty ? "bg-gold text-white hover:bg-gold-600" : "bg-beige-200 text-ink-soft cursor-not-allowed"}`} data-testid="admin-save-promos"><Save size={14}/> Guardar</button>
      </>
    }>
      <div className="grid md:grid-cols-2 gap-5">
        {list.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-beige-200 p-5" data-testid={`admin-promo-${p.id}`}>
            <div className="flex gap-4">
              <img src={p.image} alt="" className="w-24 h-24 rounded-xl object-cover flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <input className="admin-input font-serif text-lg" value={p.title} onChange={(e) => update(p.id, "title", e.target.value)} placeholder="Título"/>
                <textarea rows={2} className="admin-input" value={p.description} onChange={(e) => update(p.id, "description", e.target.value)} placeholder="Descripción" />
                <div className="grid grid-cols-2 gap-2">
                  <input className="admin-input" value={p.discount} onChange={(e) => update(p.id, "discount", e.target.value)} placeholder="20% / Ahorro 30%" />
                  <input className="admin-input" value={p.badge} onChange={(e) => update(p.id, "badge", e.target.value)} placeholder="Badge" />
                </div>
                <input className="admin-input" value={p.image} onChange={(e) => update(p.id, "image", e.target.value)} placeholder="Imagen URL" />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <button onClick={() => remove(p.id)} className="text-ink-soft hover:text-red-500 text-sm inline-flex items-center gap-1" data-testid={`admin-remove-promo-${p.id}`}><Trash2 size={14}/> Eliminar</button>
            </div>
          </div>
        ))}
      </div>
      <AdminInputStyles />
    </Panel>
  );
}

function BusinessPanel() {
  const { business, updateBusiness } = useStore();
  const [form, setForm] = useState(business);
  const [dirty, setDirty] = useState(false);
  useEffect(() => setForm(business), [business]);

  const set = (k, v) => { setForm({ ...form, [k]: v }); setDirty(true); };
  const setHour = (i, k, v) => {
    const hours = [...form.hours]; hours[i] = { ...hours[i], [k]: v };
    setForm({ ...form, hours }); setDirty(true);
  };
  const setSocial = (k, v) => { setForm({ ...form, social: { ...form.social, [k]: v } }); setDirty(true); };
  const setYape = (k, v) => { setForm({ ...form, yape: { ...(form.yape || {}), [k]: v } }); setDirty(true); };
  const save = () => { updateBusiness(form); setDirty(false); };

  return (
    <Panel title="Información del Negocio" actions={
      <button onClick={save} disabled={!dirty} className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm ${dirty ? "bg-gold text-white hover:bg-gold-600" : "bg-beige-200 text-ink-soft cursor-not-allowed"}`} data-testid="admin-save-business"><Save size={14}/> Guardar</button>
    }>
      <div className="grid md:grid-cols-2 gap-5">
        <FieldRow label="WhatsApp (con formato)"><input className="admin-input" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="admin-business-whatsapp" /></FieldRow>
        <FieldRow label="WhatsApp (solo dígitos, con prefijo país)"><input className="admin-input" value={form.whatsappNumberOnly} onChange={(e) => set("whatsappNumberOnly", e.target.value)} data-testid="admin-business-whatsappnum" /></FieldRow>
        <FieldRow label="Email"><input className="admin-input" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="admin-business-email" /></FieldRow>
        <FieldRow label="Dirección"><input className="admin-input" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="admin-business-address" /></FieldRow>
        <FieldRow label="Instagram URL"><input className="admin-input" value={form.social?.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} /></FieldRow>
        <FieldRow label="Facebook URL"><input className="admin-input" value={form.social?.facebook || ""} onChange={(e) => setSocial("facebook", e.target.value)} /></FieldRow>
        <FieldRow label="TikTok URL"><input className="admin-input" value={form.social?.tiktok || ""} onChange={(e) => setSocial("tiktok", e.target.value)} /></FieldRow>
        <FieldRow label="Google Maps Embed URL" full>
          <input className="admin-input" value={form.mapsEmbed} onChange={(e) => set("mapsEmbed", e.target.value)} data-testid="admin-business-maps" />
          <p className="text-xs text-ink-soft mt-1">Pega el src del iframe de Google Maps (Compartir → Insertar mapa).</p>
        </FieldRow>
      </div>

      <div className="mt-8">
        <div className="font-serif text-lg text-ink mb-3">Horarios</div>
        <div className="space-y-2">
          {form.hours?.map((h, i) => (
            <div key={i} className="grid grid-cols-2 gap-3" data-testid={`admin-hours-${i}`}>
              <input className="admin-input" value={h.day} onChange={(e) => setHour(i, "day", e.target.value)} />
              <input className="admin-input" value={h.time} onChange={(e) => setHour(i, "time", e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      {/* YAPE settings */}
      <div className="mt-10 p-6 rounded-2xl border border-[#702f8a]/20 bg-gradient-to-br from-white to-[#fbf6ff]" data-testid="admin-yape-panel">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#702f8a] to-[#4f1f6b] text-white flex items-center justify-center">
              <Smartphone size={18} />
            </div>
            <div>
              <div className="font-serif text-lg text-ink">Configuración Yape</div>
              <div className="text-xs text-ink-soft">Datos para el depósito de reserva.</div>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.yape?.enabled}
              onChange={(e) => setYape("enabled", e.target.checked)}
              className="w-4 h-4 accent-[#702f8a]"
              data-testid="admin-yape-enabled"
            />
            <span className="text-sm text-ink">Yape habilitado</span>
          </label>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <FieldRow label="Titular Yape">
            <input className="admin-input" value={form.yape?.holder || ""} onChange={(e) => setYape("holder", e.target.value)} data-testid="admin-yape-holder" />
          </FieldRow>
          <FieldRow label="Número Yape (con formato)">
            <input className="admin-input" value={form.yape?.phone || ""} onChange={(e) => setYape("phone", e.target.value)} data-testid="admin-yape-phone" />
          </FieldRow>
          <FieldRow label="Monto depósito (S/)">
            <input type="number" className="admin-input" value={form.yape?.depositAmount || 0} onChange={(e) => setYape("depositAmount", Number(e.target.value))} data-testid="admin-yape-amount" />
          </FieldRow>
          <FieldRow label="URL imagen del QR Yape">
            <input className="admin-input" value={form.yape?.qr || ""} onChange={(e) => setYape("qr", e.target.value)} data-testid="admin-yape-qr" />
          </FieldRow>
          <FieldRow label="Nota / Mensaje al cliente" full>
            <textarea rows={2} className="admin-input" value={form.yape?.note || ""} onChange={(e) => setYape("note", e.target.value)} data-testid="admin-yape-note" />
          </FieldRow>
        </div>

        {form.yape?.qr && (
          <div className="mt-4 flex items-center gap-4 p-3 rounded-xl bg-white border border-beige-200">
            <img src={form.yape.qr} alt="Vista previa QR" className="w-20 h-20 rounded-lg object-contain border border-beige-200 bg-white" />
            <div className="text-xs text-ink-soft">
              <div className="font-medium text-ink mb-1">Vista previa QR</div>
              Sube tu QR de Yape a un servicio público (ej. <a href="https://imgur.com" target="_blank" rel="noreferrer" className="text-[#702f8a] underline">imgur.com</a>) y pega la URL aquí.
            </div>
          </div>
        )}
      </div>
      <AdminInputStyles />
    </Panel>
  );
}

function ReservationsPanel() {
  const [list, setList] = useState(() => JSON.parse(localStorage.getItem("aura_reservations") || "[]"));

  const clear = () => {
    if (!window.confirm("¿Borrar todas las reservas guardadas localmente?")) return;
    localStorage.removeItem("aura_reservations"); setList([]);
  };

  return (
    <Panel title={`Reservas (${list.length})`} actions={
      list.length > 0 ? <button onClick={clear} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-beige-300 text-sm text-ink-soft hover:text-red-500" data-testid="admin-clear-reservations"><Trash2 size={14}/> Borrar todo</button> : null
    }>
      {list.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-beige-200">
          <div className="text-ink-soft">Aún no hay reservas registradas localmente.</div>
          <p className="text-xs text-ink-soft mt-2">Las reservas se envían por WhatsApp y se guardan en este navegador como historial.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-beige-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-beige-50 text-ink-soft text-xs uppercase tracking-luxe">
              <tr>
                <th className="text-left px-4 py-3">Fecha solicitud</th>
                <th className="text-left px-4 py-3">Nombre</th>
                <th className="text-left px-4 py-3">Teléfono</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Servicio</th>
                <th className="text-left px-4 py-3">Fecha cita</th>
                <th className="text-left px-4 py-3">Hora</th>
                <th className="text-left px-4 py-3">Comentarios</th>
              </tr>
            </thead>
            <tbody>
              {list.slice().reverse().map((r, i) => (
                <tr key={i} className="border-t border-beige-100" data-testid={`admin-reservation-${i}`}>
                  <td className="px-4 py-3 text-ink-soft">{new Date(r.createdAt).toLocaleString("es-CO")}</td>
                  <td className="px-4 py-3">{r.nombre}</td>
                  <td className="px-4 py-3">{r.telefono}</td>
                  <td className="px-4 py-3">{r.email}</td>
                  <td className="px-4 py-3">{r.serviceLabel}</td>
                  <td className="px-4 py-3">{r.fecha}</td>
                  <td className="px-4 py-3">{r.hora}</td>
                  <td className="px-4 py-3 text-ink-soft text-xs">{r.comentarios || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/* ----- Helpers ----- */

function Panel({ title, actions, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-serif text-3xl text-ink">{title}</h2>
        <div className="flex items-center gap-2">{actions}</div>
      </div>
      {children}
    </div>
  );
}

function FieldRow({ label, full, children }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-xs tracking-luxe uppercase text-ink-soft mb-2">{label}</div>
      {children}
    </label>
  );
}

function AdminInputStyles() {
  return (
    <style>{`
      .admin-input {
        width: 100%;
        padding: 0.55rem 0.85rem;
        border-radius: 0.6rem;
        border: 1px solid #F1E9D9;
        background: #fff;
        font-size: 0.875rem;
        color: #2C2825;
        outline: none;
        transition: all 0.2s ease;
        font-family: 'Outfit', sans-serif;
      }
      .admin-input:focus {
        border-color: #D4AF37;
        box-shadow: 0 0 0 3px rgba(212,175,55,0.1);
      }
    `}</style>
  );
}
